import { useCallback, useMemo } from "react";
import type { GroundSupplier } from "@b2bportal/ground-api";
import { useCarsAvailabilityFilters } from "@tenant/cars";
import { CarsRawEventName, useEmitCoreCarsRawEvent } from "~core-cars/tracking";
import type {
  CarTypeFilter,
  MinimumBagCount,
  PassengerFilter,
  PriceRange,
  Specification,
} from "~core-cars/types";

export const useFilterList = () => {
  const { context, handlers } = useCarsAvailabilityFilters();

  const { isLoading, isError } = context.filteredCars;

  const trackAppliedFilter = useEmitCoreCarsRawEvent(
    CarsRawEventName.CarsAppliedFilter
  );

  const handleCarTypeFilter = useCallback(
    (val: Array<CarTypeFilter>) => {
      handlers.setCarTypeFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  const handleCancellationFilter = useCallback(
    (val: boolean) => {
      handlers.setCancellationFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  const handleSpecificationFilter = useCallback(
    (val: Specification) => {
      handlers.setSpecificationFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  const handleSupplierFilter = useCallback(
    (val: Array<GroundSupplier>) => {
      handlers.setSupplierFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  const handleMinimumBagsFilter = useCallback(
    (val: MinimumBagCount) => {
      handlers.setMinimumBagsFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  const handlePassengerFilter = useCallback(
    (val: PassengerFilter[]) => {
      handlers.setPassengerFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  const handlePriceRangeFilter = useCallback(
    (val: PriceRange) => {
      handlers.setPriceFilter(val);
      trackAppliedFilter();
    },
    [handlers, trackAppliedFilter]
  );

  return useMemo(
    () => ({
      context: {
        disabled: isLoading || isError,
        filterOptions: context.filterOptions,
        filters: context.filters,
      },
      handlers: {
        handleCarTypeFilter,
        handleCancellationFilter,
        handleSpecificationFilter,
        handleSupplierFilter,
        handleMinimumBagsFilter,
        handlePassengerFilter,
        handlePriceRangeFilter,
      },
    }),
    [
      context.filterOptions,
      context.filters,
      handleCancellationFilter,
      handleCarTypeFilter,
      handleMinimumBagsFilter,
      handlePassengerFilter,
      handlePriceRangeFilter,
      handleSpecificationFilter,
      handleSupplierFilter,
      isError,
      isLoading,
    ]
  );
};
